import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Search, FileText, History, Wrench, LogOut, User, Settings as SettingsIcon, Lock, Link2 } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { useAuth } from '../context/AuthContext';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-dark-800">
      <nav className="bg-dark-700 shadow-sm border-b border-dark-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <CreditCard className="h-8 w-8 text-primary-600" />
                <span className="ml-2 text-xl font-bold text-white">
                  Links Papello
                </span>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link
                  to="/orders"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActive('/orders')
                      ? 'border-primary-500 text-white'
                      : 'border-transparent text-gray-400 hover:border-gray-500 hover:text-gray-200'
                  }`}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Pedidos
                </Link>
                <Link
                  to="/payment-links"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActive('/payment-links')
                      ? 'border-primary-500 text-white'
                      : 'border-transparent text-gray-400 hover:border-gray-500 hover:text-gray-200'
                  }`}
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  Gerar Link
                </Link>
                <Link
                  to="/history"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActive('/history')
                      ? 'border-primary-500 text-white'
                      : 'border-transparent text-gray-400 hover:border-gray-500 hover:text-gray-200'
                  }`}
                >
                  <History className="h-4 w-4 mr-2" />
                  Histórico
                </Link>
                <Link
                  to="/conciliation"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActive('/conciliation')
                      ? 'border-primary-500 text-white'
                      : 'border-transparent text-gray-400 hover:border-gray-500 hover:text-gray-200'
                  }`}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Conciliação
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin/settings"
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActive('/admin/settings')
                        ? 'border-primary-500 text-white'
                        : 'border-transparent text-gray-400 hover:border-gray-500 hover:text-gray-200'
                    }`}
                  >
                    <SettingsIcon className="h-4 w-4 mr-2" />
                    Admin
                  </Link>
                )}
                {process.env.NODE_ENV !== 'production' && user?.role === 'admin' && (
                  <Link
                    to="/dev-tools"
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActive('/dev-tools')
                        ? 'border-purple-400 text-white'
                        : 'border-transparent text-purple-400 hover:border-purple-300 hover:text-purple-300'
                    }`}
                  >
                    <Wrench className="h-4 w-4 mr-2" />
                    Dev Tools
                  </Link>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 text-sm text-gray-300 hover:text-white focus:outline-none"
                >
                  <User className="h-4 w-4" />
                  <span className="font-medium">{user?.fullName}</span>
                  <span className="text-gray-400">({user?.role === 'admin' ? 'Admin' : 'Vendedor'})</span>
                </button>
                
                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-dark-600 rounded-md shadow-lg py-1 z-20 border border-dark-500">
                      <Link
                        to="/change-password"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-dark-500"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Lock className="h-4 w-4" />
                        Alterar Senha
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-300 hover:bg-dark-500 text-left"
                      >
                        <LogOut className="h-4 w-4" />
                        Sair
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
